import { Fragment, useState, useEffect } from 'react';
import { BellIcon, XMarkIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { connect } from 'react-redux';
import { CourierProvider } from '@trycourier/react-provider';
import { Inbox } from '@trycourier/react-inbox';
import { Menu } from '@headlessui/react';
import { navAvatar } from '../constants/Avatar';
import logo from '../svg/logo.svg';
import {
  initialize,
  isInitialized,
  openSession,
  changeUser,
  getUser,
} from '@braze/web-sdk';
const brazeToken = process.env.REACT_APP_BRAZE_API_KEY;
const baseUrl = process.env.REACT_APP_BRAZE_API_ENDPOINT;

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

let navigation = [
  { name: 'Jobs', href: '/admin/jobs', current: false },
  { name: 'Projects', href: '/admin/projects', current: false },
  { name: 'Reports', href: '#', current: false },
];
let userNavigation = [
  { name: 'Your Profile', href: '/my/profile' },
  { name: 'Dashboard', href: '/admin/dashboard' },
  { name: 'Sign out', href: '/logout' },
];

const connector = connect(
  (state, ownProps) => ({
    ...state.user,
    employer: state.user.currentEmployer,
  }),
  {}
);
const CourierProviderKey = process.env.REACT_APP_COURIER_PROVIDER_API_KEY;

function Nav({ user, employer }) {
  const [showNav, setShowNav] = useState(false);

  // Initialize Braze and set user data
  useEffect(() => {
    const initializeBraze = async () => {
      if (!isInitialized() && user?.id) {
        try {
          // Initialize Braze
          await initialize(brazeToken, {
            baseUrl,
            enableLogging: false, // Remove in production
          });

          // Open session
          openSession();

          // Set user data in Braze
          changeUser(user.auth_id);
          const brazeUser = getUser();

          if (brazeUser) {
            brazeUser.setEmail(user.email);
            if (user.first_name) brazeUser.setFirstName(user.first_name);
            if (user.last_name) brazeUser.setLastName(user.last_name);
          }
        } catch (error) {
          console.error('Error initializing Braze:', error);
        }
      }
    };

    initializeBraze();
  }, [user]);

  useEffect(() => {
    if (employer.value === 0) {
      navigation = [];
    } else {
      navigation = [
        { name: 'Jobs', href: '/admin/jobs', current: false },
        { name: 'Projects', href: '/admin/projects', current: false },
        { name: 'Reports', href: '#', current: false },
      ];
    }
  }, [employer]);

  const toggleNav = () => {
    setShowNav(!showNav);
  };

  return (
    <nav className="w-full bg-gray-800">
      <>
        <CourierProvider userId={user.id} clientKey={CourierProviderKey}>
          <div className="mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center flex flex justify-end">
              <div className="flex items-center">
                <div className="flex-shrink-0 hidden">
                  <img src={logo} alt="Productions" />
                </div>
                <div className="hidden md:block">
                  <div className="ml-10 flex items-baseline space-x-4">
                    {navigation.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className={classNames(
                          item.current
                            ? 'bg-gray-900 text-white'
                            : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                          'px-3 py-2 rounded-md text-sm font-medium'
                        )}
                        aria-current={item.current ? 'page' : undefined}
                      >
                        {item.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="ml-4 flex items-center md:ml-6">
                  <Inbox />

                  {/* Profile dropdown */}
                  <Menu as="div" className="relative ml-3">
                    <div>
                      <Menu.Button
                        onClick={toggleNav}
                        className="flex max-w-xs items-center rounded-full bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                      >
                        <span className="sr-only">Open user menu</span>
                        {navAvatar(
                          user.photo,
                          user.gender,
                          'w-8 h-8 rounded-full'
                        )}
                      </Menu.Button>
                    </div>
                    <div>
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {userNavigation.map((item) => (
                          <Menu.Item key={item.name}>
                            {({ active }) => (
                              <a
                                href={item.href}
                                className={classNames(
                                  active ? 'bg-gray-100' : '',
                                  'block px-4 py-2 text-sm text-gray-700'
                                )}
                              >
                                {item.name}
                              </a>
                            )}
                          </Menu.Item>
                        ))}
                      </Menu.Items>
                    </div>
                  </Menu>
                </div>
              </div>
              <div className="-mr-2 flex md:hidden">
                {/* Mobile menu button */}
                <button
                  className="inline-flex items-center justify-center rounded-md bg-gray-800 p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                  onClick={toggleNav}
                >
                  <span className="sr-only">Open main menu</span>
                  {showNav ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {!!showNav ? (
            <div className="md:hidden">
              <div className="space-y-1 px-2 pt-2 pb-3 sm:px-3">
                {navigation.map((item) => (
                  <button
                    key={item.name}
                    as="a"
                    href={item.href}
                    className={classNames(
                      item.current
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                      'block px-3 py-2 rounded-md text-base font-medium'
                    )}
                    aria-current={item.current ? 'page' : undefined}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
              <div className="border-t border-gray-700 pt-4 pb-3">
                <div className="flex items-center px-5">
                  <div className="flex-shrink-0">
                    {navAvatar(
                      user.photo,
                      user.gender,
                      'w-10 h-10 rounded-full'
                    )}
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium leading-none text-white">
                      {user.first_name}
                    </div>
                    <div className="text-sm font-medium leading-none text-gray-400">
                      {user.email}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="ml-auto flex-shrink-0 rounded-full bg-gray-800 p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800"
                  >
                    <span className="sr-only">View notifications</span>
                    <BellIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="mt-3 space-y-1 px-2">
                  {userNavigation.map((item) => (
                    <button
                      key={item.name}
                      as="a"
                      href={item.href}
                      className="block rounded-md px-3 py-2 text-base font-medium text-gray-400 hover:bg-gray-700 hover:text-white"
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <></>
          )}
        </CourierProvider>
      </>
    </nav>
  );
}

export default connector(Nav);
